import {
  RumConfig,
  addSessionAttributes,
  initializeRUM,
  recordError,
} from '@/lib/rum'
import { SUPPORT_SESSION_PATH, isInSupportSession } from '@/lib/support-session'
import React, { ReactNode, createContext, useContext, useEffect } from 'react'

import { useAuth } from './AuthContext'
import { useConfig } from './ConfigContext'

interface RumContextType {
  isInitialized: boolean
}

const RumContext = createContext<RumContextType>({ isInitialized: false })

export const useRum = () => {
  return useContext(RumContext)
}

interface RumProviderProps {
  children: ReactNode
}

export const RumProvider: React.FC<RumProviderProps> = ({ children }) => {
  const config = useConfig()
  const auth = useAuth()
  const [rumInitialized, setRumInitialized] = React.useState(false)

  // Building the SDK config is a pure mapping of the app config, so it is
  // derived rather than recomputed inside the effect. That also takes the
  // "nothing to initialize" branch out of the effect body, which used to call
  // setIsInitialized synchronously — the cascading render the compiler flags.
  const rumConfig = React.useMemo<RumConfig | null>(() => {
    // A staff member observing as a customer would be recorded as that
    // customer, so a support-session tab records nothing (droidmap#2651),
    // starting with the page that exchanges its one-time code.
    if (
      !config?.RUM_CONFIG ||
      isInSupportSession() ||
      window.location.pathname === SUPPORT_SESSION_PATH
    ) {
      return null
    }
    return {
      applicationId: config.RUM_CONFIG.applicationId,
      applicationVersion: config.RUM_CONFIG.applicationVersion || '1.0.0',
      region: config.RUM_CONFIG.region,
      identityPoolId: config.RUM_CONFIG.identityPoolId,
      sessionSampleRate: config.RUM_CONFIG.sessionSampleRate || 1,
      telemetries: config.RUM_CONFIG.telemetries || [
        'errors',
        'performance',
        'http',
      ],
      allowCookies: config.RUM_CONFIG.allowCookies !== false,
      enableXRay: config.RUM_CONFIG.enableXRay === true,
    }
  }, [config])

  // "Nothing to initialize" settles to the same observable state as "finished
  // initializing" — consumers only wait on this so they don't record events
  // before the SDK exists. Deriving it keeps the no-config case a render-time
  // conclusion instead of a state write. It stays false until the app config
  // itself has loaded, matching the old `if (config && ...)` guard.
  const isInitialized = rumInitialized || (!!config && rumConfig === null)

  useEffect(() => {
    if (!rumConfig || rumInitialized) {
      return
    }

    initializeRUM(rumConfig)
      .then(() => {
        setRumInitialized(true)
      })
      .catch((error) => {
        console.warn('RUM initialization failed:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
        })
        setRumInitialized(true)
      })
  }, [rumConfig, rumInitialized])

  useEffect(() => {
    if (config && !rumConfig) {
      console.warn(
        'No RUM configuration available, skipping RUM initialization',
      )
    }
  }, [config, rumConfig])

  // Add user attributes when authenticated
  useEffect(() => {
    if (isInitialized && auth.user) {
      addSessionAttributes({
        userId: auth.user.username,
      })
    }
  }, [auth.user, isInitialized])

  // Global error handler for RUM
  useEffect(() => {
    if (!isInitialized) {
      return
    }

    const handleError = (event: ErrorEvent) => {
      recordError(event.error || event.message)
    }

    const handleRejection = (event: PromiseRejectionEvent) => {
      recordError(event.reason)
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [isInitialized])

  return (
    <RumContext.Provider value={{ isInitialized }}>
      {children}
    </RumContext.Provider>
  )
}
